import { message as antdMessage } from 'antd';
import axios, { AxiosError, AxiosResponse } from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import isNil from 'lodash/isNil';
import { REHYDRATE } from 'redux-persist';
import type { SagaIterator } from 'redux-saga';
import {
  all,
  call,
  fork,
  put,
  select,
  takeEvery,
  takeLatest
} from 'redux-saga/effects';

import { triggerClient } from 'clients';
import { isInactiveSamplePointErrorMessage } from 'clients/utils/is-inactive-sample-point-error-message';
import { showInactiveSamplePointErrorModal } from 'store/modules/layout/actions';
import NetworkActionTypes from 'store/modules/network/constants';
import { selectNetworkOnline } from 'store/modules/network/selectors';
import {
  makeSelectHasHeaterHost,
  makeSelectIsAdvancedDam,
  makeSelectSamplePointById
} from 'store/modules/samplePoints/selectors';
import { PendingIsEnabledTriggerChange, TriggersState } from 'store/modules/triggers/types';
import SamplePoint from 'types/models/samplePoint';
import Trigger from 'types/models/trigger';
import { APIResponseParsed } from 'types/response';
import makeTriggerSpecialKey from 'utils/make-trigger-special-key';
import {
  deleteRequest,
  patchRequest,
  postRequest
} from 'utils/redux-saga-requests';
import { getTriggerId } from 'utils/Trigger/get-trigger-id';
import { parseSpecialKey } from 'utils/Trigger/parse-special-key';

import {
  editTrigger,
  editTriggerFailure,
  editTriggerSuccess,
  enableTriggerBulk,
  enableTriggerBulkSuccess,
  enableTriggerRequest,
  enableTriggerSuccess,
  removePendingIsEnabledChange,
  removeTrigger,
  removeTriggerFailure,
  removeTriggers,
  setPendingIsEnabledChange,
  setPendingIsEnabledChangeBulk,
  setTrigger
} from './actions';
import ActionTypes from './constants';
import {
  selectEnabledTriggersKeyedBySpecialKey,
  selectPendingIsEnabledChanges
} from './selectors';
import { getCalculatedVolumeMappings } from '../volumeMappings/actions';
import { createVolumeMappingQueryFromTrigger } from '../volumeMappings/helper';
import { VolumeMappingQuery } from '../volumeMappings/types';

export function* queueEnableAlertTrigger(
  action: ReturnType<typeof enableTriggerRequest>
) {
  const {
    payload: { samplePointId, eventLevel, valueType, conditionType, values }
  } = action;

  const specialKey = makeTriggerSpecialKey(
    samplePointId,
    eventLevel,
    valueType,
    conditionType
  );

  yield all([
    put(setPendingIsEnabledChange(specialKey, { ...values, valueType })),
    put(enableTriggerSuccess(samplePointId))
  ]);
}

export function* watchEnableAlertTriggerRequest() {
  yield takeEvery(
    ActionTypes.ENABLE_ALERT_TRIGGER_REQUEST,
    queueEnableAlertTrigger
  );
}

export function* queueEnableAlertTriggerBulk(
  action: ReturnType<typeof enableTriggerBulk>
) {
  const {
    payload: { triggerOptions }
  } = action;

  // Iterate over triggerOptions, collecting 'specialKeys', trigger values &
  // samplePointIds in a single pass.
  const [specialKeys, triggerValues, samplePointIds] = triggerOptions.reduce(
    (
      [specialKeysAcc, valuesAcc, samplePointIdsAcc],
      { samplePointId, eventLevel, valueType, conditionType, values }
    ) => [
        [
          ...specialKeysAcc,
          makeTriggerSpecialKey(
            samplePointId,
            eventLevel,
            valueType,
            conditionType
          )
        ],
        [...valuesAcc, { ...values, valueType }],
        [...samplePointIdsAcc, samplePointId]
      ],
    [[], [], []] as [string[], any[], number[]]
  );

  yield all([
    put(setPendingIsEnabledChangeBulk(specialKeys, triggerValues)),
    put(enableTriggerBulkSuccess(samplePointIds))
  ]);
}

export function* watchEnableAlertTriggerBulkRequest() {
  yield takeEvery(
    ActionTypes.ENABLE_ALERT_TRIGGER_BULK_REQUEST,
    queueEnableAlertTriggerBulk
  );
}

/**
 * Special keys currently being processed by a run of
 * processEnabledTriggersUpdateQueue. Module-level (not per-call) so it is
 * shared across overlapping runs spawned by takeEvery -- e.g. the user
 * toggles a second trigger, or the network reconnects, while an earlier
 * run's API call for the SAME key is still in flight. Without this a second
 * run can snapshot and submit the same key concurrently, which can either
 * drop whichever run's clear loses the race or double-POST a create.
 */
const specialKeysBeingProcessed = new Set<string>();

// Edit triggers
export function* processEnabledTriggersUpdateQueue(): SagaIterator {
  const networkOnline: boolean | undefined = yield select(selectNetworkOnline);
  const triggers: Record<string, Trigger> = yield select(
    selectEnabledTriggersKeyedBySpecialKey
  );

  const pendingIsEnabledChanges: TriggersState['pendingIsEnabledChanges'] =
    yield select(selectPendingIsEnabledChanges);

  const specialKeys: string[] = Object.keys(pendingIsEnabledChanges);

  if (networkOnline) {
    for (const specialKey of specialKeys) {
      // Another concurrent run already owns this key; leave it alone rather
      // than submitting it twice. It will be picked up by whichever run
      // (this one or a later one) is still active once the owner finishes.
      if (specialKeysBeingProcessed.has(specialKey)) {
        // eslint-disable-next-line no-continue
        continue;
      }

      const { samplePointId, eventLevel, valueType, conditionType } = parseSpecialKey(specialKey);
      const samplePoint: SamplePoint | undefined = yield select(makeSelectSamplePointById(samplePointId));
      const belongsToHeaterHost: boolean = yield select(makeSelectHasHeaterHost(samplePointId));

      if (!samplePoint && !belongsToHeaterHost) continue;

      // Mutable: updated after a create, so a newer value re-processed below
      // (same key, same claim) is treated as an update rather than a second
      // create.
      let triggerId: number | undefined = getTriggerId(
        samplePointId,
        eventLevel,
        valueType,
        conditionType,
        triggers
      );

      specialKeysBeingProcessed.add(specialKey);
      try {
        /**
         * Holds the claim across as many submissions as it takes for the
         * pending value to stop changing underneath it. SET_TRIGGER /
         * REMOVE_TRIGGER only clear pendingIsEnabledChanges when the value
         * they are about to clear is still the one this run just submitted
         * (`clearPending`) -- a value re-selected fresh right after the API
         * call, not the stale closure from before it. A newer toggle that
         * lands mid-request is caught by that comparison and re-submitted on
         * the next loop instead of being silently dropped (Mitch, PR #2372).
         */
        // eslint-disable-next-line no-constant-condition
        while (true) {
          const latestPendingIsEnabledChanges: TriggersState['pendingIsEnabledChanges'] =
            yield select(selectPendingIsEnabledChanges);
          const pendingChange: PendingIsEnabledTriggerChange | undefined =
            latestPendingIsEnabledChanges[specialKey];
          // Nothing left to process for this key (already cleared).
          if (!pendingChange) break;
          const claimedChange = cloneDeep(pendingChange);

          if (!triggerId && isNil(claimedChange.value)) {
            yield put(removePendingIsEnabledChange(specialKey));
            break;
          }
          const volumeMappingsQuery: VolumeMappingQuery = isNil(claimedChange.value)
            ? {}
            : createVolumeMappingQueryFromTrigger(
              claimedChange.value,
              claimedChange.valueType
            );

          let clearedThisValue = true;
          try {
            if (triggerId && claimedChange.value === null) {
              // # Delete trigger
              yield call(deleteRequest, `trigger/${triggerId}`);
              const afterCall: TriggersState['pendingIsEnabledChanges'] =
                yield select(selectPendingIsEnabledChanges);
              clearedThisValue = isEqual(afterCall[specialKey], claimedChange);
              yield put(removeTrigger({ triggerId, specialKey, clearPending: clearedThisValue }));
            } else if (triggerId) {
              // # Update trigger
              const response: AxiosResponse<Trigger> = yield call(patchRequest, `trigger/${triggerId}`, {
                value: claimedChange.value,
                duration: claimedChange.duration,
                useForecast: claimedChange.useForecast,
                valueType: claimedChange.valueType,
                hourCountStart: claimedChange.hourCountStart,
                window: claimedChange.window
              });
              const { data } = response;
              const updatedTrigger: Trigger = {
                ...data,
                value: data.value
              };
              const afterCall: TriggersState['pendingIsEnabledChanges'] =
                yield select(selectPendingIsEnabledChanges);
              clearedThisValue = isEqual(afterCall[specialKey], claimedChange);
              yield put(setTrigger({ trigger: updatedTrigger, specialKey, clearPending: clearedThisValue }));
            } else {
              // # Add trigger
              // At the moment all the triggers have the same values for
              // thresholdSensitivity and timeBeforeReTrigger.
              // timeBeforeReTrigger is different for battery alerts, but we don't
              // create it from FE.
              const response: AxiosResponse<Trigger> = yield call(postRequest, 'trigger', {
                eventLevel: Number(eventLevel),
                isEnabled: true,
                timeBeforeReTrigger: 604800,
                samplePointId: Number(samplePointId),
                valueType,
                thresholdSensitivity: 5,
                hourCountStart: claimedChange.hourCountStart,
                window: claimedChange.window ?? 3600,
                duration: claimedChange.duration,
                conditionType: Number(conditionType),
                value: claimedChange.value,
                useForecast: claimedChange.useForecast,
                message: claimedChange.message,
                triggerType: claimedChange.triggerType
              });
              const { data } = response;
              const newTrigger: Trigger = {
                ...data,
                value: data.value
              };
              triggerId = newTrigger.id;
              const afterCall: TriggersState['pendingIsEnabledChanges'] =
                yield select(selectPendingIsEnabledChanges);
              clearedThisValue = isEqual(afterCall[specialKey], claimedChange);
              yield put(setTrigger({ trigger: newTrigger, specialKey, clearPending: clearedThisValue }));
            }

            const spIsAdvancedDam: boolean = yield select(makeSelectIsAdvancedDam(samplePointId));
            if (spIsAdvancedDam && volumeMappingsQuery) {
              yield put(
                getCalculatedVolumeMappings(samplePointId, volumeMappingsQuery)
              );
            }
          } catch (error) {
            if (!axios.isAxiosError(error)) throw error;
            /**
             * Suppress error handling in the event of a Network Error.
             */
            if (error.message !== 'Network Error') {
              if (!claimedChange.isToastMuted) {
                const message = get(
                  error,
                  'response.data.message',
                  'Sorry, something went wrong.'
                );

                const isInactiveSamplePointError = isInactiveSamplePointErrorMessage(message);
                if (isInactiveSamplePointError) {
                  yield put(showInactiveSamplePointErrorModal());
                } else {
                  antdMessage.error('Failed to update alert trigger. Please try again');
                }
              }

              yield put(removePendingIsEnabledChange(specialKey));
            }
            break;
          }

          // Cleared and nothing newer arrived while we were submitting: done
          // with this key. Otherwise the pending value we just saw IS the
          // newer one (clearPending above was false, so it's still live) --
          // loop and submit it too, still holding the claim.
          if (clearedThisValue) break;
        }
      } finally {
        specialKeysBeingProcessed.delete(specialKey);
      }
    }
  }
}

export function* watchSetPendingIsEnableChange() {
  /**
   * takeEvery, not takeLatest: processEnabledTriggersUpdateQueue loops
   * sequentially over every pending trigger change, making a real API call
   * (delete/patch/postRequest) per item. takeLatest CANCELS that generator
   * whenever this action fires again (e.g. the user toggles a second
   * trigger while the first's request is still in flight, or the network
   * comes back online mid-loop) -- the in-flight request still completes
   * server-side, but the cancelled generator never runs
   * removePendingIsEnabledChange/setTrigger/removeTrigger for it, so the
   * next run reads it as still-pending and resubmits the same change.
   * Mirrors the same fix in notifications/saga.ts and eventReasons/saga.ts.
   */
  yield takeEvery(
    [
      REHYDRATE,
      NetworkActionTypes.SET_NETWORK_ONLINE,
      ActionTypes.SET_PENDING_IS_ENABLE_CHANGE,
      ActionTypes.SET_PENDING_IS_ENABLE_CHANGE_BULK
    ],
    processEnabledTriggersUpdateQueue
  );
}

export function* requestRemoveTriggers(
  action: ReturnType<typeof removeTriggers>
) {
  const {
    payload: { triggerIds }
  } = action;
  try {
    if (triggerIds.length) {
      yield all(triggerIds.map((id) => call(deleteRequest, `trigger/${id}`)));
    }
  } catch (error) {
    antdMessage.error('Failed to remove alert triggers');
    const message = get(
      error,
      'response.data.message',
      'Sorry, something went wrong.'
    );
    yield put(removeTriggerFailure(message, error as AxiosError));
  }
}

export function* watchRemoveTriggers() {
  yield takeLatest(ActionTypes.REMOVE_TRIGGERS, requestRemoveTriggers);
}

// Cannot reuse the processEnabledTriggersUpdateQueue above
// because LiquidFertiliserFlowRateTriggers can toggle between on and off states, without ever being deleted.
// The processEnabledTriggersUpdateQueue above processes only the 'enabledTriggers' in the store.
function* requestEditTrigger(action: ReturnType<typeof editTrigger>) {
  const { payload: { id, values, isToastMuted } } = action;
  const response: APIResponseParsed<Trigger> = yield call(triggerClient.editTrigger, id, values);
  if (response.data) {
    yield all([
      put(setTrigger({ trigger: response.data, specialKey: '' })), // specialKey is not used because this function does not go through the pendingChange queue
      put(editTriggerSuccess())
    ]);
    if (!isToastMuted) antdMessage.success('Alert trigger updated');
  } else {
    const message = response.error.message || 'Failed to update alert trigger';
    yield put(editTriggerFailure(message));
    if (!isToastMuted) antdMessage.error('Failed to update alert trigger');
  }
}

function* watchEditTriggerRequest() {
  yield takeEvery(ActionTypes.EDIT_TRIGGER_REQUEST, requestEditTrigger);
}

export default function* triggersSaga() {
  yield all([
    fork(watchEnableAlertTriggerRequest),
    fork(watchEnableAlertTriggerBulkRequest),
    fork(watchSetPendingIsEnableChange),
    fork(watchRemoveTriggers),
    fork(watchEditTriggerRequest)
  ]);
}
